import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { ScheduledEmailModel } from '@app/contacts/shared/scheduled-email.model';
import { ScheduledEmailService } from '@app/contacts/shared/scheduled-email.service';

/**
 * TVG-359: the emails this contact is still due, beside the TVG-313 list of
 * the ones already sent.
 *
 * The QA run on 20 October 2026 could not test the TVG-335 follow-up sequence
 * or the TVG-338 post-viewing email because nothing in the browser showed that
 * either had been queued, when it would go, or why one had been stopped. This
 * panel is exactly that and nothing more: read-only, with no cancel, no edit
 * and no send.
 */
@Component({
  selector: 'app-contact-scheduled-emails',
  standalone: true,
  imports: [DatePipe, NgClass],
  templateUrl: './contact-scheduled-emails.component.html',
})
export class ContactScheduledEmailsComponent implements OnInit, OnChanges {
  @Input({ required: true }) contactId!: number;

  protected scheduledEmails: ScheduledEmailModel[] = [];
  protected loading = false;
  protected loadError: string | null = null;

  constructor(private scheduledEmailService: ScheduledEmailService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contactId'] && !changes['contactId'].firstChange) {
      this.scheduledEmails = [];
      this.load();
    }
  }

  protected statusClasses(scheduledEmail: ScheduledEmailModel): string {
    switch (scheduledEmail.status) {
      case 'pending':
        return 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300';
      case 'queued':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
      case 'cancelled':
        return 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-white/70';
      case 'failed':
        return 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300';
      default:
        return 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300';
    }
  }

  private load(): void {
    if (!this.contactId) {
      return;
    }

    this.loading = true;
    this.loadError = null;

    this.scheduledEmailService.listForContact(this.contactId).subscribe({
      next: (response) => {
        this.scheduledEmails = response.data ?? [];
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        // A 404 here means the follow-up feature is off in this environment,
        // which is not an error a tester needs to see as a red box.
        this.loadError = error?.status === 404 ? null : 'Failed to load queued emails';
      },
    });
  }
}
